import { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/machinesim/api';

function apiErr(e) {
  const fromBody =
    e.response?.data &&
    typeof e.response.data === 'object' &&
    e.response.data.error != null
      ? String(e.response.data.error)
      : null;
  return fromBody || e.message || 'Request failed';
}

export default function StreamErpFleetTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /** When true, simulator list still loads; ERP columns show a DB-unavailable hint */
  const [erpUnavailable, setErpUnavailable] = useState(false);
  const [erpUnavailableDetail, setErpUnavailableDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const simRes = await axios.get(`${API}/machines`);
        if (cancelled) return;
        const simMachines = simRes.data.machines || [];

        let erpMachines = [];
        let erpFailed = false;
        let erpDetail = null;
        try {
          const erpRes = await axios.get(`${API}/erp/machines`);
          erpMachines = erpRes.data.machines || [];
        } catch (e) {
          erpFailed = true;
          erpDetail = apiErr(e);
        }
        if (cancelled) return;

        setErpUnavailable(erpFailed);
        setErpUnavailableDetail(erpDetail);

        const byErpId = new Map();
        for (const e of erpMachines) {
          if (e.erpMachineId) {
            byErpId.set(e.erpMachineId, e);
          }
        }

        const merged = simMachines.map((m) => {
          if (erpFailed) {
            return {
              simulatorName: m.name,
              execution: m.state?.execution ?? '—',
              erpName: null,
              machineType: null,
              linked: false,
              erpMissing: true,
            };
          }
          const erp = byErpId.get(m.name);
          return {
            simulatorName: m.name,
            execution: m.state?.execution ?? '—',
            erpName: erp?.name ?? '—',
            machineType: erp?.machineType ?? null,
            linked: !!erp,
            erpMissing: false,
          };
        });

        setRows(merged);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(apiErr(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="p-8 text-muted-foreground">Loading fleet…</div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-destructive">{error}</div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <h2 className="text-lg font-semibold text-foreground mb-4">Simulator fleet vs ERP</h2>
      <p className="text-sm text-muted-foreground mb-6 max-w-3xl">
        ERP <span className="font-mono text-foreground">machineType</span> appears when an ERP machine row exists
        whose <span className="font-mono text-foreground">erpMachineId</span> equals the simulator machine name
        (e.g. SPM-3000-1). Most simulator-only assets have no ERP row yet.
      </p>

      {erpUnavailable && (
        <div
          className="mb-6 rounded-lg border border-cn-warning/40 bg-cn-warning/10 px-4 py-3 text-sm text-foreground"
          role="status"
        >
          <p className="font-medium text-cn-warning">ERP machine list unavailable</p>
          <p className="mt-1">
            ERP data lives in PostgreSQL (via the simulator API). The simulator fleet below still comes from the
            in-memory machine service. {erpUnavailableDetail ? `Server said: ${erpUnavailableDetail}` : ''} If you
            expect ERP rows here, start Postgres and ensure{' '}
            <span className="font-mono text-foreground">DATABASE_URL</span> in <span className="font-mono">server/.env</span>{' '}
            is valid, then run migrations and seed.
          </p>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-secondary text-left text-muted-foreground">
              <th className="p-3 font-medium">Simulator name</th>
              <th className="p-3 font-medium">Execution</th>
              <th className="p-3 font-medium">ERP resource</th>
              <th className="p-3 font-medium">ERP type</th>
            </tr>
          </thead>
          <tbody className="font-mono text-foreground">
            {rows.map((r) => (
              <tr key={r.simulatorName} className="border-t border-border">
                <td className="p-3 text-primary">{r.simulatorName}</td>
                <td className="p-3">{r.execution}</td>
                <td className="p-3">
                  {r.erpMissing ? (
                    <span className="text-cn-warning italic">—</span>
                  ) : r.linked ? (
                    r.erpName
                  ) : (
                    '—'
                  )}
                </td>
                <td className="p-3">
                  {r.erpMissing ? (
                    <span className="text-cn-warning italic text-xs">ERP DB unavailable</span>
                  ) : r.linked ? (
                    (r.machineType ?? '—')
                  ) : (
                    <span className="text-muted-foreground italic">Not linked in ERP</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
